import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Trash2, Plus, Edit2, Check, X, MapPin
} from "lucide-react";
import MapAddressPicker from "@/components/MapAddressPicker";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { token, logout, language, setLanguage, t } = useApp();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [addAddressDialog, setAddAddressDialog] = useState(false);
  const [newAddress, setNewAddress] = useState({ label: "Home", street: "", city: "" });
  const [addressSaving, setAddressSaving] = useState(false);
  const [restaurantInfo, setRestaurantInfo] = useState({ latitude: null, longitude: null, address: "" });
  const [reportMsg, setReportMsg] = useState("");
  const [reportSending, setReportSending] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      if (!token) {
        navigate("/auth");
        return;
      }
      const res = await api.get("/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
      setEditForm({
        full_name: res.data.full_name || "",
        email: res.data.email || "",
        phone: res.data.phone || "",
      });
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
        navigate("/auth");
      } else {
        toast.error(t("Failed to load profile"));
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, logout]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    api.get("/menu/restaurant-info").then(res => {
      setRestaurantInfo({
        latitude: res.data?.latitude ?? null,
        longitude: res.data?.longitude ?? null,
        address: res.data?.address ?? "",
      });
    }).catch(() => {});
  }, []);

  const handleSaveProfile = async () => {
    if (!editForm.full_name || !editForm.email || !editForm.phone) {
      toast.error(t("Fill all fields"));
      return;
    }
    setSaving(true);
    try {
      const res = await api.put(
        "/auth/profile",
        {
          full_name: editForm.full_name,
          email: editForm.email,
          phone: editForm.phone,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser(res.data);
      setEditDialog(false);
      toast.success(t("Profile updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Failed to update profile"));
    } finally {
      setSaving(false);
    }
  };

  const handleAddAddress = async () => {
    if (!newAddress.street || !newAddress.city) {
      toast.error(t("Fill all address fields"));
      return;
    }

    setAddressSaving(true);
    try {
      const res = await api.post(
        "/auth/addresses",
        { ...newAddress },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser(res.data);
      setAddAddressDialog(false);
      setNewAddress({ label: "Home", street: "", city: "" });
      toast.success(t("Address added"));
    } catch (e) {
      if (e.response?.status === 400 && e.response?.data?.detail?.duplicate) {
        toast.error(t("You already have this address saved"));
      } else {
        toast.error(e.response?.data?.detail || t("Failed to add address"));
      }
    } finally {
      setAddressSaving(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm(t("Delete this address?"))) return;
    try {
      const res = await api.delete(`/auth/addresses/${addressId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
      toast.success(t("Address deleted"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Failed to delete address"));
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      const res = await api.put(`/auth/addresses/${addressId}/default`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
      toast.success(t("Default address updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Could not update default address"));
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm(t("Are you sure you want to delete your account? This cannot be undone."))) return;
    try {
      await api.delete("/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t("Your account has been deleted"));
      logout();
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Could not delete account"));
    }
  };

  const handleSendReport = async () => {
    const msg = reportMsg.trim();
    if (!msg) { toast.error(t("Please write a message first")); return; }
    setReportSending(true);
    try {
      await api.post("/auth/report-issue", { message: msg });
      toast.success(t("Thanks! Your message has been sent."));
      setReportMsg("");
    } catch {
      toast.error(t("Could not send. Please try again."));
    } finally {
      setReportSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-full bg-[#0A0A0A] flex items-center justify-center relative z-[1]">
        <Loader2 className="w-8 h-8 text-[#D4B483] animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-full bg-[#0A0A0A] flex items-center justify-center relative z-[1]">
        <div className="text-center">
          <p className="text-[#D4D4D8] mb-4">{t("Not logged in")}</p>
          <Button onClick={() => navigate("/")} className="bg-[#D4B483] text-[#0A0A0A]">
            {t("Go Home")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#0A0A0A] relative z-[1]">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#121212] to-[#0A0A0A] border-b border-[#27272A]">
        <div className="max-w-4xl mx-auto px-4 py-8">
      <Button
        onClick={() => navigate("/")}
        variant="ghost"
        className="mb-6 text-[#D4B483] hover:bg-[#1A1A1A]"
      >
        <ArrowLeft className="w-4 h-4 mr-2" /> {t("Back")}
      </Button>
      <span className="lux-eyebrow">{t("Bästa Biten")}</span>
      <h1 className="font-playfair text-4xl md:text-5xl text-[#F5F1E8] mt-4 mb-2 lux-title">
        {t("My Profile")}
      </h1>
      <p className="text-[#D4D4D8] italic font-light">{t("Manage your details and addresses")}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="space-y-8">
          {/* Personal Information */}
          <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-playfair text-2xl font-bold text-[#EDEDED]">
                {t("Personal Info")}
              </h2>
              <Button
                onClick={() => setEditDialog(true)}
                className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5]"
              >
                <Edit2 className="w-4 h-4 mr-2" /> {t("Edit")}
              </Button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-[#D4D4D8] text-sm font-semibold mb-2">{t("NAME")}</p>
                <p className="text-[#EDEDED] text-lg">{user.full_name}</p>
              </div>
              <div>
                <p className="text-[#D4D4D8] text-sm font-semibold mb-2">{t("EMAIL")}</p>
                <p className="text-[#EDEDED] text-lg">{user.email}</p>
              </div>
              <div>
                <p className="text-[#D4D4D8] text-sm font-semibold mb-2">{t("PHONE")}</p>
                <p className="text-[#EDEDED] text-lg">{user.phone}</p>
              </div>
              <div>
                <p className="text-[#D4D4D8] text-sm font-semibold mb-2">{t("VERIFIED")}</p>
                <Badge
                  className={`${
                    user.email_verified
                      ? "bg-[#10B981]/15 text-[#10B981]"
                      : "bg-[#F59E0B]/15 text-[#F59E0B]"
                  } border-0`}
                >
                  {user.email_verified ? t("Yes") : t("No")}
                </Badge>
              </div>
            </div>
            {!user.email_verified && (
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/[0.06] p-4">
                <p className="text-[#FBBF77] text-sm">{t("Your email is not verified yet.")}</p>
                <button
                  onClick={async () => {
                    try {
                      await api.post("/auth/resend-email-verification");
                      toast.success(t("Verification email sent"));
                    } catch (e) {
                      toast.error(e.response?.data?.detail || t("Something went wrong"));
                    }
                  }}
                  className="text-[#D4B483] hover:text-[#E5CFA5] text-sm font-semibold whitespace-nowrap"
                >
                  {t("Resend verification email")}
                </button>
              </div>
            )}
          </div>

          {/* Addresses */}
          <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-playfair text-2xl font-bold text-[#EDEDED]">
                {t("My Addresses")}
              </h2>
              <Button
                onClick={() => setAddAddressDialog(true)}
                className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5]"
              >
                <Plus className="w-4 h-4 mr-2" /> {t("Add")}
              </Button>
            </div>

            {user.addresses && user.addresses.length > 0 ? (
              <div className="space-y-3">
                {user.addresses.map((addr) => (
                  <div
                    key={addr.id}
                    className="bg-[#0A0A0A] rounded-lg border border-[#27272A] p-4 flex items-start justify-between group hover:border-[#3F3F46] transition-colors"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-[#EDEDED] font-medium">{addr.street}</p>
                        <Badge className="bg-[#3B82F6]/15 text-[#93C5FD] border-0">{addr.label || "Other"}</Badge>
                        {user.default_address_id === addr.id && (
                          <Badge className="bg-[#10B981]/15 text-[#10B981] border-0">{t("Standard")}</Badge>
                        )}
                      </div>
                      <p className="text-[#D4D4D8] text-sm mt-1">
                        {addr.city}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all">
                      {user.default_address_id !== addr.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefaultAddress(addr.id)}
                          className="text-[#D4B483] hover:text-[#E5CFA5]"
                        >
                          {t("Set as default")}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAddress(addr.id)}
                        className="text-[#A8ABB2] hover:text-[#EF4444]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-[#D4D4D8] mb-4">{t("No address saved")}</p>
                <Button
                  onClick={() => setAddAddressDialog(true)}
                  className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5]"
                >
                  {t("Add first address")}
                </Button>
              </div>
            )}
          </div>

          {/* Language */}
          <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-8">
            <h2 className="font-playfair text-2xl font-bold text-[#EDEDED] mb-6">
              {t("Language")}
            </h2>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => setLanguage("sv")}
                variant={language === "sv" ? "default" : "outline"}
                className={language === "sv"
                  ? "bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5]"
                  : "border-[#27272A] text-[#EDEDED] hover:bg-[#1A1A1A]"}
              >
                {t("Switch to Swedish")}
              </Button>
              <Button
                onClick={() => setLanguage("en")}
                variant={language === "en" ? "default" : "outline"}
                className={language === "en"
                  ? "bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5]"
                  : "border-[#27272A] text-[#EDEDED] hover:bg-[#1A1A1A]"}
              >
                {t("Switch to English")}
              </Button>
            </div>
          </div>

          {/* Account Actions */}
          <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-8">
            <h2 className="font-playfair text-2xl font-bold text-[#EDEDED] mb-6">
              {t("Account Management")}
            </h2>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => {
                  logout();
                  navigate("/");
                  toast.success(t("Logged out"));
                }}
                variant="outline"
                className="border-[#27272A] text-[#EDEDED] hover:bg-[#1A1A1A]"
              >
                {t("Logout")}
              </Button>
              <Button
                onClick={handleDeleteAccount}
                variant="outline"
                className="border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 hover:border-[#EF4444]"
              >
                {t("Delete Account")}
              </Button>
            </div>
          </div>

          {/* Report a problem */}
          <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-8">
            <h2 className="font-playfair text-2xl font-bold text-[#EDEDED] mb-2">
              {t("Report a problem")}
            </h2>
            <p className="text-[#D4D4D8] text-sm mb-4">
              {t("Tell us if you experience any disruptions or issues with the website.")}
            </p>
            <Textarea
              value={reportMsg}
              onChange={(e) => setReportMsg(e.target.value)}
              placeholder={t("Describe the problem…")}
              maxLength={3000}
              rows={4}
              className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] resize-none mb-3"
            />
            <Button
              onClick={handleSendReport}
              disabled={reportSending || !reportMsg.trim()}
              className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] disabled:opacity-50"
            >
              {reportSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t("Send message")}
            </Button>
          </div>
        </div>
      </div>

      {/* Edit Profile Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="bg-[#121212] border-[#27272A]">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED] font-playfair">
              {t("Edit profile")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#D4D4D8] text-xs mb-1.5 block">{t("Name")}</Label>
              <Input
                value={editForm.full_name}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, full_name: e.target.value }))
                }
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11"
              />
            </div>
            <div>
              <Label className="text-[#D4D4D8] text-xs mb-1.5 block">{t("Email")}</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, email: e.target.value }))
                }
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11"
              />
            </div>
            <div>
              <Label className="text-[#D4D4D8] text-xs mb-1.5 block">
                {t("Phone")}
              </Label>
              <Input
                type="tel"
                value={editForm.phone}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, phone: e.target.value }))
                }
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setEditDialog(false)}
                className="flex-1 border-[#27272A] text-[#EDEDED] hover:bg-[#1A1A1A]"
              >
                <X className="w-4 h-4 mr-2" /> Avbryt
              </Button>
              <Button
                onClick={handleSaveProfile}
                disabled={saving}
                className="flex-1 bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" /> {t("Save")}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Address Dialog */}
      <Dialog open={addAddressDialog} onOpenChange={setAddAddressDialog}>
        <DialogContent className="bg-[#121212] border-[#27272A]">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED] font-playfair">
              {t("Add address")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#D4D4D8] text-xs mb-1.5 block">{t("Address type")}</Label>
              <select
                value={newAddress.label}
                onChange={(e) =>
                  setNewAddress((f) => ({ ...f, label: e.target.value }))
                }
                className="w-full bg-[#0A0A0A] border border-[#27272A] text-[#EDEDED] h-11 rounded-md px-3"
              >
                <option value="Home">{t("Home")}</option>
                <option value="Work">{t("Work")}</option>
                <option value="Other">{t("Other")}</option>
              </select>
            </div>
            <MapAddressPicker
              restaurantLat={restaurantInfo.latitude}
              restaurantLng={restaurantInfo.longitude}
              restaurantAddress={restaurantInfo.address}
              onAddressChange={(addr) =>
                setNewAddress((f) => ({ ...f, street: addr.street || addr.display, city: addr.city || "" }))
              }
            />
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setAddAddressDialog(false)}
                className="flex-1 border-[#27272A] text-[#EDEDED] hover:bg-[#1A1A1A]"
              >
                <X className="w-4 h-4 mr-2" /> Avbryt
              </Button>
              <Button
                onClick={handleAddAddress}
                disabled={addressSaving}
                className="flex-1 bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold"
              >
                {addressSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" /> {t("Add")}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
